import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { MailCheck, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import LogoSwipRent from "@/components/brand/logo_swiprent";
import InputField from "@/components/forms/input_field";
import ButtonMain from "@/components/buttons/button_main";

const schema = z.object({
  email: z.string().trim().min(1, "Enter your email").email("Enter a valid email"),
});

// Request a password reset link. We never reveal whether an email exists: the
// backend always answers 200 with a neutral message, and this screen always
// shows the same confirmation regardless of the result.
export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  async function onSubmit(values) {
    try {
      await api.post("/auth/forgot-password", { email: values.email.trim() });
    } catch {
      // Even on a transport error we show the neutral confirmation, so the
      // screen can never be used to probe which emails are registered.
    }
    setSent(true);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-day px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
      >
        <Link to="/" className="inline-block">
          <LogoSwipRent />
        </Link>

        {sent ? (
          <div className="mt-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-tint text-brand-lime">
              <MailCheck size={22} />
            </span>
            <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-brand-ink">Check your email</h1>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              If an account exists for that email, we have sent a link to reset your password. The link
              expires in one hour. If you do not see it, check your spam or junk folder.
            </p>
            <Link
              to="/auth/login"
              className="mt-7 inline-flex items-center gap-1.5 text-sm font-extrabold text-brand-lime transition hover:text-brand-lime"
            >
              <ArrowLeft size={15} /> Back to log in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="mt-6 text-2xl font-extrabold tracking-tight text-brand-ink">Forgot your password?</h1>
            <p className="mt-1 text-sm font-semibold text-slate-500">
              Enter your email and we will send you a link to reset it.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
              <InputField
                label="Email"
                type="email"
                placeholder="you@example.com"
                error={errors.email?.message}
                {...register("email")}
              />
              <ButtonMain type="submit" full disabled={isSubmitting}>
                {isSubmitting ? "Sending" : "Send reset link"}
              </ButtonMain>
            </form>

            <p className="mt-6 text-center text-sm font-semibold text-slate-500">
              Remembered it?{" "}
              <Link to="/auth/login" className="font-extrabold text-brand-lime transition hover:text-brand-lime">
                Back to log in
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
